import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useState, useEffect } from "react";
import API from "../api/api01";
import { toast } from "sonner";
import {
  DollarSign,
  CreditCard,
  TrendingUp,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
} from "lucide-react";
import { InvoiceDetailModal } from "./InvoiceDetailModal";

interface Account {
  id: string;
  leadName: string;
  course: string;
  totalFees: number;
  paidAmount: number;
  balanceAmount: number;
  paymentStatus: "Paid" | "Partial" | "Pending" | "Overdue";
  paymentMethod: "Online" | "Cash" | "Cheque" | "DD";
  lastPaymentDate: string;
  dueDate: string;
  counselor: {
    name: string;
    avatar: string;
  };
}

export function LeadAccounts() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await API.get("/lead-accounts/");
      setAccounts(response.data || []);
    } catch (err) {
      console.error("Failed to fetch lead accounts", err);
      toast.error("Failed to load account data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Paid":
        return <Badge className="bg-green-600 text-white">Paid</Badge>;
      case "Partial":
        return <Badge className="bg-blue-600 text-white">Partial</Badge>;
      case "Pending":
        return <Badge className="bg-yellow-600 text-white">Pending</Badge>;
      case "Overdue":
        return <Badge className="bg-red-600 text-white">Overdue</Badge>;
      default:
        return null;
    }
  };

  const calculateStats = () => {
    const totalRevenue = accounts.reduce((sum, acc) => sum + acc.paidAmount, 0);
    const totalOutstanding = accounts.reduce((sum, acc) => sum + acc.balanceAmount, 0);
    const paidCount = accounts.filter(acc => acc.paymentStatus === "Paid").length;
    const overdueCount = accounts.filter(acc => acc.paymentStatus === "Overdue").length;

    return { totalRevenue, totalOutstanding, paidCount, overdueCount };
  };

  const stats = calculateStats();

  const filteredAccounts = accounts.filter((account) => {
    const matchesSearch =
      account.leadName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.course.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      statusFilter === "all" || account.paymentStatus === statusFilter;

    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-4 md:p-6 space-y-6 bg-slate-50 dark:bg-[#020617] min-h-full">
      {/* Header */}
      <div>
        <h1>Lead Accounts</h1>
        <p className="text-muted-foreground mt-1">
          Manage financial accounts and payment tracking for leads
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-500/10">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{(stats.totalRevenue / 1000).toFixed(0)}k</p>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-orange-500/10">
                <DollarSign className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{(stats.totalOutstanding / 1000).toFixed(0)}k</p>
                <p className="text-sm text-muted-foreground">Outstanding</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-primary/10">
                <CheckCircle2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.paidCount}</p>
                <p className="text-sm text-muted-foreground">Fully Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-500/10">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.overdueCount}</p>
                <p className="text-sm text-muted-foreground">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
  <CardContent className="p-4">
    <div className="flex flex-col md:flex-row gap-4">
      {/* Search Input */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" />
        <Input
          placeholder="Search accounts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 w-full rounded-lg bg-slate-50 dark:bg-[#020617] border border-slate-200 dark:border-slate-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Payment Status Select */}
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-full md:w-48 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 flex items-center">
          <Filter className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
          <SelectValue placeholder="Payment Status" />
        </SelectTrigger>
        <SelectContent className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md">
          <SelectItem value="all" className="hover:bg-gray-100 dark:hover:bg-gray-700">All Status</SelectItem>
          <SelectItem value="Paid" className="hover:bg-gray-100 dark:hover:bg-gray-700">Paid</SelectItem>
          <SelectItem value="Partial" className="hover:bg-gray-100 dark:hover:bg-gray-700">Partial</SelectItem>
          <SelectItem value="Pending" className="hover:bg-gray-100 dark:hover:bg-gray-700">Pending</SelectItem>
          <SelectItem value="Overdue" className="hover:bg-gray-100 dark:hover:bg-gray-700">Overdue</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </CardContent>
</Card>


      {/* Accounts Table */}
      <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Account Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-[#020617] border-b border-slate-200 dark:border-slate-800">
                  <TableHead>Lead Name</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead className="text-right">Total Fees</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Due Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={account.counselor.avatar} />
                          <AvatarFallback>
                            {account.leadName.substring(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{account.leadName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{account.course}</TableCell>
                    <TableCell className="text-right font-medium">
                      ₹{account.totalFees.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      ₹{account.paidAmount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-orange-600">
                      ₹{account.balanceAmount.toLocaleString()}
                    </TableCell>
                    <TableCell>{getStatusBadge(account.paymentStatus)}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {account.dueDate}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full hover:bg-primary/5 hover:text-primary transition-all active:scale-95"
                        onClick={() => {
                          setSelectedAccount(account);
                          setIsInvoiceModalOpen(true);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Invoice
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Detail Portal */}
      <InvoiceDetailModal
        isOpen={isInvoiceModalOpen}
        onOpenChange={setIsInvoiceModalOpen}
        account={selectedAccount}
      />
    </div>
  );
}
